import { motion } from 'framer-motion';
import { useCallback, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { clsm, isElementsOverlapping } from '../../../utils';
// import { DEFAULT_PROFILE_VIEW_TRANSITION } from '../../../constants';
import { useChannel } from '../../../contexts/Channel';
import { usePlayerContext } from '../contexts/Player';
import { useProfileViewAnimation } from '../contexts/ProfileViewAnimation';
// import { useUser } from '../../../contexts/User';
import PlayerOverlay from './PlayerOverlay';
// import UserAvatar from '../../../components/UserAvatar';
// import { POPUP_ID } from './Controls/RenditionSetting';
import useResize from '../../../hooks/useResize';
// import StatusItem from '../../../components/StatusBar/StatusItem/StatusItem';
// import { Visibility } from '../../../assets/icons';
import ViewerCount from '../../../components/ViewerCount/viewerCount';

const getHeaderButtonClasses = (shouldRemoveZIndex = false) => {
  return clsm([
    'flex',
    'items-center',
    'space-x-2',
    'shrink-0',
    'pointer-events-all',
    !shouldRemoveZIndex && 'z-10'
  ]);
};

const PlayerHeader = ({
  avatarSrc = '',
  color = 'default',
  username = '',
  openPopupIds = [],
  stagePlayerVisible = false
}) => {
  const { isPlayerMuted } = useSelector((state) => state.channel);
  const {
    getProfileViewAnimationProps,
    headerAnimationControls,
    // isProfileViewAnimationEnabled,
    isProfileViewExpanded,
    shouldAnimateProfileView
    // toggleProfileView
  } = useProfileViewAnimation();
  const isRenditionSettingPopupExpanded = '';
  // const isRenditionSettingPopupExpanded = !!openPopupIds.find(
  //   (openPopupId) => openPopupId === POPUP_ID
  // );
  const [shouldRemoveFollowButtonZIndex, setShouldRemoveFollowButtonZIndex] =
    useState(false);
  const { qualitiesContainerRef } = usePlayerContext();

  useResize(() => {
    if (isRenditionSettingPopupExpanded && qualitiesContainerRef?.current) {
      if (
        !shouldRemoveFollowButtonZIndex &&
        isElementsOverlapping(qualitiesContainerRef?.current)
      ) {
        setShouldRemoveFollowButtonZIndex(true);
      }

      if (
        shouldRemoveFollowButtonZIndex &&
        !isElementsOverlapping(qualitiesContainerRef?.current)
      ) {
        setShouldRemoveFollowButtonZIndex(false);
      }
    }
  });

  const qualitiesContainerInitialHeight =
    qualitiesContainerRef?.current?.clientHeight;

  // On mount
  useEffect(() => {
    if (isRenditionSettingPopupExpanded && qualitiesContainerRef?.current) {
      if (
        isElementsOverlapping(qualitiesContainerRef?.current) ||
        qualitiesContainerRef?.current.clientHeight >
          qualitiesContainerInitialHeight
      ) {
        setShouldRemoveFollowButtonZIndex(true);
      }
    }
  }, [
    qualitiesContainerRef,
    isRenditionSettingPopupExpanded,
    qualitiesContainerInitialHeight,
    shouldRemoveFollowButtonZIndex
  ]);

  useEffect(() => {
    if (!isRenditionSettingPopupExpanded) {
      setShouldRemoveFollowButtonZIndex(false);
    }
  }, [isRenditionSettingPopupExpanded]);

  const { isOverlayVisible } = usePlayerContext();
  // const { isSessionValid } = useUser();
  const { channelData: { isLive: isBroadcastLive, stageId } = {} } =
    useChannel();
  const layoutDependency = useRef(null);
  // const animationDuration = DEFAULT_PROFILE_VIEW_TRANSITION.duration;
  const isLive = isBroadcastLive || !!stageId;
  const shouldShowHeaderOverlay =
    isOverlayVisible || isLive === false || isProfileViewExpanded;

  if (shouldAnimateProfileView.current) layoutDependency.current = Date.now();

  const getPlayerHeaderProfileViewAnimationProps = useCallback(
    (variantStyles) =>
      getProfileViewAnimationProps(headerAnimationControls, variantStyles),
    [getProfileViewAnimationProps, headerAnimationControls]
  );

  return (
    <div
      className={clsm(
        isPlayerMuted && 'z-50',
        'absolute',
        'w-full',
        'top-0',
        'flex',
        'pt-8',
        'px-8',
        'lg:pt-4',
        'lg:px-4',
        'pointer-events-none',
        'items-center',
        isProfileViewExpanded
          ? ['justify-center', 'flex-col']
          : ['justify-between', 'flex-row']
      )}
    >
      <PlayerOverlay
        isGradientVisible={!isProfileViewExpanded}
        isVisible={shouldShowHeaderOverlay}
        position="top"
      >
        <motion.div
          {...getPlayerHeaderProfileViewAnimationProps({
            expanded: { y: 184, desktop: { y: 168 } },
            collapsed: { y: 0 }
          })}
          className={clsm(
            'flex',
            'items-center',
            'w-full',
            isProfileViewExpanded
              ? ['justify-center', 'flex-col']
              : ['justify-end', 'flex-row'] // change to end from between
          )}
        >
          <motion.div
            layout="position"
            layoutDependency={layoutDependency.current}
            className={clsm(
              getHeaderButtonClasses(shouldRemoveFollowButtonZIndex)
            )}
            {...getPlayerHeaderProfileViewAnimationProps({
              expanded: { marginTop: 24, marginLeft: 0 },
              collapsed: { marginTop: 0, marginLeft: 16 }
            })}
          >
            <ViewerCount />
          </motion.div>
        </motion.div>
      </PlayerOverlay>
    </div>
  );
};

PlayerHeader.propTypes = {
  avatarSrc: PropTypes.string,
  stagePlayerVisible: PropTypes.bool,
  color: PropTypes.string,
  openPopupIds: PropTypes.arrayOf(PropTypes.string),
  username: PropTypes.string
};

export default PlayerHeader;
